<template>
  <div class="wrapper__detail">
    <q-linear-progress v-if="this.reloading" indeterminate />
    <div class="header">
      <div
        class="header__top"
        :style="{width: this.workSetsCollapsed ? '100%' : 'calc(100% - 35px)'}"
      >
        <div class="header__top_left">
          <BackBtn @click="this.handleBack" />
          <h1 class="title" @click="() => this.reloadData()">
            Схема этажа {{ this.activeData?.floor }}
            <q-icon name="replay" class="reloadIcon" />
          </h1>
        </div>
        <div
          style="display: flex; gap: 10px; align-items: center"
          class="apartmentList"
        >
          <q-btn-toggle
            v-if="this.apartments?.length"
            v-model="this.activeFloorFlatId"
            toggle-color="primary"
            :options="getFlatOptions()"
            @update:model-value="onFlatChange"
            size="sm"
            padding="sm"
          />
          <Clickable
            v-if="this.apartments?.length"
            :disable="!this.activeFlat?.kvlist"
            :link="true"
            :href="this.getKVListLink()"
            class="header__link"
          >
            <q-btn
              size="sm"
              color="primary"
              :disable="!this.activeFlat?.kvlist"
            >
              <q-tooltip> Квартирный лист </q-tooltip>
              <q-icon size="2em" color="#fff" name="list_alt_icon" />
            </q-btn>
          </Clickable>
        </div>
      </div>

      <div class="header__head" style="width: 100%">
        <div class="resident" v-if="!!this.activeData">
          <span>{{ this.activeData?.resident_name }}</span>
          |
          <span>{{ this.activeData?.entrance_name }}</span>
          <template v-if="this.activeData?.external_resource_code">
            |
            <span>{{ this.activeData?.external_resource_code }}</span>
          </template>
          <template v-if="this.floorSchema?.floor_map?.wall_height">
            |
            <span
              >{{ `h = ${this.floorSchema?.floor_map?.wall_height}` }}
              <q-tooltip>Высота потолка</q-tooltip></span
            >
          </template>
        </div>
      </div>
    </div>

    <q-btn-toggle
      v-model="this.tab"
      toggle-color="primary"
      :options="this.tabs"
      @update:model-value="onTabChange"
      size="sm"
      style="margin-bottom: 15px; margin-left: 20px;"
    />
    <Schema
      v-if="tab==='schema'"
      :floorSchema="floorSchema"
      :schemaFetching="this.schemaFetching"
      :activeFloorFlatId="this.activeFloorFlatId"
      :flatBorders="this.flatBorders"
      :workSetParams="this.workSetParams"
      :floorMapId="this.floorMapId"
      :commentPoints="this.commentPoints"
      :params="this.params"
      :floorMapInfo="this.floorMapInfo"
    />
    <FloorDetailMaterials
      v-else-if="this.tab === 'materials'"
      :floorMapId="this.floorMapId"
    />
    <FloorOpenspace
      v-else-if="this.tab === 'openspace'"
      :floorMapId="this.floorMapId"
    />
    <FloorWorkSets
      v-if="!!this.floorMapId && tab==='schema'"
      :is-fetching="this.workSetsFetching"
      :info="this.workSetsData"
      :onChange="this.onWorkSetsChange"
      :collapsed="this.workSetsCollapsed"
      @toggleCollapsed="e => workSetsCollapsed = !workSetsCollapsed"
      :floor-map-id="this.floorMapId"
      :document-params="this.params"
      :project-id="params?.project_id || activeData?.project_id"
      :selectable="true"
      :reloadData="this.getWorkSets"
      :selected="this.activeWorkSetId"
      :onSelect="value => this.onWorkSetSelect(value)"
      :pointsData="{fetchingAllPoints: this.fetchingAllPoints, 
        fetchingPoints: this.fetchingPoints, getData: this.getCommentPoints, params: this.commentPointsParams}"
    />
  </div>
</template>

<script>
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar';
import BackBtn from '@/components/common/BackBtn.vue';
import Clickable from '@/components/common/Clickable.vue';
import CustomDropdown from "@/components/common/CustomDropdown";
import CustomSelect from "@/components/form/CustomSelect";
import { PAGES } from '@/constants';
import Schema from "./Schema.vue";
import FloorDetailMaterials from "./FloorDetailMaterials.vue";
import FloorOpenspace from "./FloorOpenspace.vue";
import { getFloorSchema, getFloorWorkSetParams, getFloorWorkSets, getFloorMapPoints } from './services';
import FloorWorkSets from "./WorkSets.vue";
import CustomTabs from "@/components/common/CustomTabs";

export default {
  name: "ResidentialFloorDetail",
  components: { CustomTabs, BackBtn, CustomDropdown, CustomSelect, Clickable, FloorOpenspace, FloorWorkSets, Schema, FloorDetailMaterials },
  props: ['goBack', 'activeData', 'params', 'updateData'],
  data() {
    return {
      PAGES,
      schemaFetching: false,
      reloading: false,
      floorSchema: null,
      floorMapId: null,
      floorMapInfo: null,
      workSetsFetching: false,
      workSetsData: null,
      activeWorkSetId: null,
      workSetParams: null,
      activeFloorFlatId: null,
      activeFlat: null,
      commentPoints: null,
      commentPointsParams: null,
      flatBorders: false,
      fetchingAllPoints: false,
      workSetsCollapsed: !(window.innerWidth < 991),
      fetchingPoints: {},
      tab: 'schema',
      tabs: [{label: 'Cхема этажа', value: 'schema'}, {label: 'Материалы', value: 'materials'}, {label: 'Openspace', value: 'openspace'}],
    };
  },
  computed:{
    apartments() {
      return this.floorSchema?.flat?.map(item => ({...item, label: `кв. ${item.flat_num}`}))
    },
  },
  methods: {
    onTabChange(tab) {
      this.tab = tab
    },
    async getFloorSchemaData(floor_map_id) {
      if(!floor_map_id) return
      this.schemaFetching = true
      const res = await getFloorSchema(floor_map_id)
      this.schemaFetching = false
      this.floorSchema = res || null
      this.floorMapInfo = res?.floor_map || null
    },
    async getWorkSets() {
      if(!this.floorMapId) return
      this.workSetsFetching = true
      const res = await getFloorWorkSets(this.floorMapId)
      this.workSetsFetching = false
      this.workSetsData = res || []
    },
    async onWorkSetsChange(res) {
      this.workSetsData = res || {}
      this.getFloorSchemaData(this.floorMapId)
    },
    async getCommentPoints(params = null, reset = false) {
      if(reset) {
        this.fetchingAllPoints = false
        this.fetchingPoints = {}
        this.commentPoints = null
        this.commentPointsParams = null
        return
      }
      this.commentPointsParams = params || {}
      if(params) this.fetchingPoints[params.placement_type_id + '_' + params.work_set_check_group_id] = true
      else this.fetchingAllPoints = true
      const res = await getFloorMapPoints(this.floorMapId, params || {})
      if(params) this.fetchingPoints[params.placement_type_id + '_' + params.work_set_check_group_id] = false
      else this.fetchingAllPoints = false
      if(!res) {
        this.commentPointsParams = null
        return
      }
      this.commentPoints = res
    },
    async onWorkSetSelect(workSetId) {
      if(this.activeWorkSetId === workSetId) {
        this.activeWorkSetId = null
        this.workSetParams = []
        return
      }
      this.activeWorkSetId = workSetId
      this.schemaFetching = true
      const res = await getFloorWorkSetParams(this.floorMapId, workSetId)
      this.schemaFetching = false
      this.workSetParams = res || []
    },
    getFlatOptions() {
      const all = {
        label: 'Все',
        floor_flat_id: null,
        value: null,
      }
      if(!this.apartments?.length) return [all]
      return [all, ...this.apartments?.map(item => ({...item, value: item.floor_flat_id}))]
    },
    onFlatChange(floor_flat_id, flat) {
      if(!floor_flat_id) {
        this.activeFlat = null
        this.activeFloorFlatId = null || null
        return
      }
      this.activeFlat = flat
      this.activeFloorFlatId = floor_flat_id || null
      this.flatBorders = false
    },
    updateFloorSchema(res) {
      if(res?.floor_map?.floor_map_id) {
         this.floorMapId = res?.floor_map?.floor_map_id
         this.floorMapInfo = res?.floor_map
        }
      this.floorSchema = res
    },
    handleBack() {
      this.floorSchema = null
      this.goBack()
    },
    getKVListLink() {
      const title = encodeURIComponent(`${this.activeData?.resident_name}, ${this.activeData?.entrance_name}, этаж ${this.activeData?.floor}, кв ${this.activeFlat?.flat_num}`)
      return `${PAGES.imageUrl}?url=${this.activeFlat?.kvlist}&title=${title}`
    },
    async reloadData() {
      if(this.reloading) return
      this.reloading = true
      await this.getFloorSchemaData(this.activeData.floor_map_id)
      this.reloading = false
    }
  },
  mounted() {
    this.floorMapId = this.activeData.floor_map_id
    this.getFloorSchemaData(this.activeData.floor_map_id)
    this.getWorkSets()
  },
  unmounted() {
    if(this.updateData)
      this.updateData()
  },
  setup() {
    const {showSucceedSnackbar} = useCustomSnackbar()
    return {showSucceedSnackbar}
  }
};
</script>

<style scoped lang="scss">
.wrapper__detail {
  position: relative;
}
.header {
  padding: 20px;
  &__link {
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    i {
      display: inline-block;
    }
  }
}
.header__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  position: relative;

  @media (max-width: 991px) {
    width: 100% !important;
  }
}
.apartmentList {
  @media (max-width: 991px) {
    display: none !important;
  }
}
.header__top {
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 1400px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  &_left {
    display: flex;
    align-items: center;
    gap: 20px;
    @media (max-width: 1500px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }
}
.title {
  font-size: 24px;
  line-height: 27px;
  font-weight: 500;
  margin-top: 0;
  margin-bottom: 0;
  cursor: pointer;
  transition: all .3s;

  &:hover {
    color: rgb(5, 104, 0);
  }
}
.reloadIcon {
  font-size: 22px;
  color: rgb(5, 104, 0);
}
.resident {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #898989;
  flex-wrap: wrap;

  span {
    font-size: 13px;
    color: #898989
  }
}
.asideActions {
  position: absolute;
  top: 100%;
  margin-top: 10px;
  left: -10px;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background-color: rgba(255, 255, 255, .8);
  padding: 10px;
  border-radius: 5px;
}
</style>
